import type { AuthUser } from '../types.js';

export interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  current_active_company?: string;
  companyUuid?: string;
  company_id?: string;
  exp?: number;
  [key: string]: unknown;
}

function claimString(
  payload: Record<string, unknown> | JwtPayload | null | undefined,
  key: string,
): string | null {
  if (!payload) return null;
  const value = payload[key];
  return typeof value === 'string' && value ? value : null;
}

export function resolveCompanyIdFromClaims(
  payload: Record<string, unknown> | JwtPayload | null | undefined,
): string | null {
  return (
    claimString(payload, 'current_active_company') ??
    claimString(payload, 'companyUuid') ??
    claimString(payload, 'company_id')
  );
}

export function decodeJwtPayloadUnsafe(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== 'string') return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = Buffer.from(payload.replace(/-/g, '+').replace(/_/g, '/'), 'base64').toString(
      'utf8',
    );
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
}

export function isJwtExpired(token: string, bufferSec: number): boolean {
  const payload = decodeJwtPayloadUnsafe(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now + bufferSec;
}

export function mapClaimsToUser(payload: JwtPayload | null): AuthUser | null {
  if (!payload?.sub) return null;

  const name =
    payload.name ??
    ([payload.given_name, payload.family_name].filter(Boolean).join(' ') || null);

  return {
    id: payload.sub,
    email: payload.email ?? null,
    name,
    picture: payload.picture ?? null,
    companyId: resolveCompanyIdFromClaims(payload),
  };
}

export function getUserFromFeJwt(token: string | null | undefined): AuthUser | null {
  return mapClaimsToUser(decodeJwtPayloadUnsafe(token));
}
